import React, { useRef, useState } from 'react';
import { User } from '../../../types/manageUsers';
import { HiDownload, HiUpload, HiInformationCircle, HiX } from 'react-icons/hi';
import { usersToCSV, downloadCSV, readFileAsText, parseCSVEmails } from '../../../utils/csvUtils';

interface CsvUploadSectionProps {
  users: User[];
  editableUserIds?: string[];
  isJira: boolean;
  overrides?: Record<string, string>;
  onEmailsUploaded: (emails: Record<string, string>) => void;
}

const CsvUploadSection: React.FC<CsvUploadSectionProps> = ({
  users,
  editableUserIds = [],
  isJira,
  overrides = {},
  onEmailsUploaded,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastFileName, setLastFileName] = useState<string | null>(null);

  const handleDownloadCSV = () => {
    const csvContent = usersToCSV(users, editableUserIds, overrides);
    const filename = `users-needing-email_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const processFile = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setLastFileName(file.name);

    try {
      const csvContent = await readFileAsText(file);
      const { emails, errors } = parseCSVEmails(csvContent, isJira, users, editableUserIds);
      
      if (errors && errors.length > 0) {
        setUploadError(errors.join(' | '));
        return;
      }

      if (Object.keys(emails).length === 0) {
        setUploadError('No valid email mappings found in CSV file');
        return;
      }

      onEmailsUploaded(emails);
      setUploadSuccess(`Imported emails for ${Object.keys(emails).length} user(s)`);
    } catch (error) {
      console.error('CSV parsing error:', error);
      setUploadError('Failed to parse CSV file. Please check the format.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full space-y-4">
      <div
        className={`p-3 bg-blue-50 dark:bg-blue-900/20 border rounded-lg ${isDragging ? 'border-blue-400 dark:border-blue-500 ring-2 ring-blue-300' : 'border-blue-200 dark:border-blue-800'}`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (!file) return;
          if (!file.name.toLowerCase().endsWith('.csv')) {
            setUploadError('Only CSV files are supported. Please drop a .csv file.');
            return;
          }
          processFile(file);
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-300">CSV helper</span>
          </div>
          <span className="text-[11px] text-blue-800/80 dark:text-blue-300/80">Export template ➜ fill emails ➜ upload</span>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={handleDownloadCSV}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <HiDownload className="w-3 h-3 mr-1" />
            Export as CSV
          </button>

          <button
            onClick={triggerFileUpload}
            disabled={isUploading}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <HiUpload className="w-3 h-3 mr-1" />
            {isUploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        </div>

        <div className="mt-2 text-[11px] text-blue-800/80 dark:text-blue-300/80 flex items-center justify-between">
          {isUploading ? (
            <span className="inline-flex items-center">
              <svg className="animate-spin mr-1 h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              Processing CSV...
            </span>
          ) : (
            <>
              <span>Or drag & drop a .csv file here</span>
              {lastFileName && (
                <span className="ml-2 inline-flex items-center gap-2">
                <span className="font-medium"> •{lastFileName}</span>
                  <button
                    type="button"
                    onClick={() => { setLastFileName(null); setUploadError(null); setUploadSuccess(null); if (fileInputRef.current) fileInputRef.current.value=''; }}
                    className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200"
                    title="Clear file"
                  >
                    <HiX className="w-3 h-3" />
                  </button>
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileUpload}
        className="hidden"
      />

      {uploadError && (
        <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-800 dark:text-red-300">
          {uploadError}
        </div>
      )}

      {uploadSuccess && (
        <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs text-green-800 dark:text-green-300">
          {uploadSuccess}
        </div>
      )}
    </div>
  );
};

export default CsvUploadSection;
